import { columnChooserMenuItemDef } from "$root/third-party-integrations/ag-grid/ag-grid";
import { qualifier } from "$root/utils";
import { CircularProgressBar, type IconName, type IconProps, Row } from "@mdotm/shadcn/legacy/components";
import { IconButton } from "@mdotm/shadcn/mdotm/components";
import type { CustomCellRendererProps, CustomHeaderProps } from "ag-grid-react";

export const columnChooserActionLoadingContextKey =
	"columnChooserActionLoadingContextKey-34922238-ce93-4e62-8d3a-e9ae5e26b11a" as const;

export function settingsColdDef<T>(): {
	iconButton<P>(icon: IconName, action: (params: P) => void): (params: P) => React.JSX.Element;
	columnChooser(params: CustomHeaderProps<T>): React.JSX.Element;
	columnMenuChooser(params: CustomHeaderProps<T>): React.JSX.Element;
	columnMenuChooserWithLoading(params: CustomHeaderProps<T>): React.JSX.Element;
	contextMenuTrigger(params: CustomCellRendererProps<T>): React.JSX.Element;
	deleteButton<TDeletable extends { deleted?: boolean }>(
		params: CustomCellRendererProps<TDeletable>,
	): React.JSX.Element;
} {
	function iconButton<P>(icon: IconName | IconProps, action: (params: P) => void) {
		const spec: IconProps = typeof icon === "string" ? { icon, size: 16 } : icon;
		return function IconCellButton(params: P) {
			return (
				<Row className="h-full w-full" alignItems="center" justifyContent="center">
					<IconButton
						unstyled
						data-qualifier={qualifier.component.button("iconButton")}
						onClick={() => action(params)}
						icon={spec.icon}
						size={spec.size}
						color={spec.color}
						// Legacy put this on the glyph. `IconButton` styles the button, and the only
						// object-form caller passes a rotation, which renders the same either way.
						style={spec.style}
					/>
				</Row>
			);
		};
	}

	const columnChooser = iconButton("Settings", (e: CustomHeaderProps<T>) => columnChooserMenuItemDef.action(e));
	const columnMenuChooser = iconButton("Settings", (params: CustomHeaderProps<T>) =>
		params.api.showColumnMenu(params.column.getColId()),
	);

	function columnMenuChooserWithBusy(params: CustomHeaderProps<T>): React.JSX.Element {
		const loading = Boolean(
			(params.context as Record<string, unknown> | undefined)?.[columnChooserActionLoadingContextKey],
		);
		if (loading) {
			return (
				<Row className="h-full w-full" alignItems="center" justifyContent="center">
					<CircularProgressBar value="indeterminate" outerDiameter={16} />
				</Row>
			);
		}
		return columnMenuChooser(params);
	}

	const contextMenuTrigger = iconButton<CustomCellRendererProps<T>>(
		{
			icon: "More",
			size: 20,
			style: { transform: "rotateZ(90deg)" },
		},
		(params) =>
			params.api.showContextMenu({ source: "api", value: params.value, column: params.column, rowNode: params.node }),
	);

	function deleteButton<TDeletable extends { deleted?: boolean }>(params: CustomCellRendererProps<TDeletable>) {
		if (params.data?.deleted) {
			return (
				<Row className="h-full w-full" alignItems="center">
					<IconButton unstyled data-qualifier={qualifier.component.button("restore")} icon="restore" size={16} />
				</Row>
			);
		}

		return (
			<Row className="h-full w-full" alignItems="center">
				<IconButton unstyled data-qualifier={qualifier.component.button("delete")} icon="Delete" size={16} />
			</Row>
		);
	}

	return {
		iconButton,
		columnChooser,
		contextMenuTrigger,
		deleteButton,
		columnMenuChooser,
		columnMenuChooserWithLoading: columnMenuChooserWithBusy,
	};
}
